import React, {
  useState,
  useEffect,
  RefObject,
} from 'react';
import SearchIcon from 'core/icons/search';
import {
  maxBillingCodeSearchQueryLength,
} from 'core/constants';
import DateRangePicker from 'core/components/DateTimePicker/DateRangePicker';
import SearchInput from 'pages/Dashboard/components/SearchInput';
import Filters from 'core/Filters';
import {
  ChargeFilterDTO,
} from 'dtos';
import {
  ClaimViews,
} from 'dtos/claimViews';
import {
  useFilterQueryWithDateRange,
  useTableReset,
} from 'core/hooks';
import WriteOffDialog, {
  WriteOffDialogProps,
} from 'pages/Dashboard/pages/Billing/Charges/WriteOffDialog';
import {
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import isNil from 'lodash/isNil';
import useStorageState from 'core/hooks/storage';
import BulkClaimsButton from 'pages/Dashboard/pages/Billing/Charges/BulkClaimsButton';
import {
  ChargesFilter,
} from 'pages/Dashboard/pages/Billing/api';
import {
  useSyncSearchQuery,
} from 'pages/Dashboard/hooks/url';
import Select from 'core/components/Select';
import ChargesView from 'pages/Dashboard/pages/Billing/Charges/ChargesView';
import ClaimsView from 'pages/Dashboard/pages/Billing/Charges/ClaimsView';
import ClaimTypeTabs from 'pages/Dashboard/pages/Billing/Charges/ClaimTypeTabs';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  DateRange,
} from 'types/dateRange';

export type ViewType = 'Charges' | 'Claims';

export type BillingViewProps = {
  dateRange: DateRange;
  searchQuery: string;
  filterQuery: ChargesFilter;
  tableInstanceRef: RefObject<VirtualTableInstance>;
};

export type ClaimsBillingViewProps = BillingViewProps & {
  claimView: ClaimViews;
};

export type ViewOption = {
  label: string;
  value: ViewType;
};

const viewOptions: ViewType[] = [
  'Charges',
  'Claims',
];

export default function Charges() {
  const [viewType, setViewType] = useStorageState<ViewType>('chargesViewType', 'Charges');
  const [claimView, setClaimView] = useStorageState<ClaimViews>('chargesClaimView', ClaimViews.All);

  const {
    reset: resetCharges,
    tableInstanceRef: chargesTableInstanceRef,
  } = useTableReset();
  const {
    reset: resetClaims,
    tableInstanceRef: claimsTableInstanceRef,
  } = useTableReset();

  const resetTables = () => {
    resetCharges();
    resetClaims();
  };

  const {
    searchQuery,
    setSearchQuery,
  } = useSyncSearchQuery({ storageKey: 'chargesSearchQuery' });
  const {
    filterQuery,
    setFilterQuery,
    filterInstanceRef,
    dateRange,
    setDateRange,
  } = useFilterQueryWithDateRange<ChargeFilterDTO>(
    'charges',
    { amount: 6, uniqueKey: 'chargesRange' },
    true,
  );

  const onFilterUpdate = () => {
    setFilterQuery(filterInstanceRef?.current?.getFilterQuery?.() ?? {});
    resetTables();
  };

  const [
    writeoffDialogParams,
    setWriteoffDialogParams,
  ] = useState<WriteOffDialogProps | null>(null);

  useEffect(() => {
    const unsubscribeFromAddWriteoff = paymentsEventChannel.on('onAddWriteoff', (params) => {
      setWriteoffDialogParams(params);
    });

    return () => {
      unsubscribeFromAddWriteoff();
    };
  }, []);

  return (
    <>
      <div className="flex h-full min-h-0 w-full min-w-0 flex-col gap-4">
        <div className="min-w-0 w-full shrink-0 overflow-x-auto px-4 py-2 -my-2">
          <div className="flex w-full min-w-max items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Select<ViewType>
                className="!border-gray-400 !rounded-md w-[132px]"
                id="charges-view-menu"
                placeholder="View"
                value={viewType}
                options={viewOptions}
                getLabel={(option: ViewType) => option}
                getValue={(option: ViewType) => option}
                onChange={(val: ViewType | ViewType[]) => {
                  setViewType(val as ViewType);
                }}
                renderValue={(val: string) => (
                  <p>
                    View:
                    {' '}
                    <span className="font-semibold">{val}</span>
                  </p>
                )}
              />
              {viewType === 'Claims' && (
                <ClaimTypeTabs
                  value={claimView}
                  onChange={(nextClaimView) => {
                    setClaimView(nextClaimView);
                    resetClaims();
                  }}
                />
              )}
              <SearchInput
                placeholder="Patient name/phone/DOB or claim number"
                className="w-60 lg:w-96"
                delay={400}
                value={searchQuery}
                onChange={(val) => {
                  setSearchQuery(val);
                  resetTables();
                }}
                inputProps={{ autoComplete: 'off', maxLength: maxBillingCodeSearchQueryLength }}
                icon={<SearchIcon className="!w-4 !h-4 text-gray-400" />}
                id="patient-search"
              />
              <DateRangePicker
                maxDate={null}
                value={dateRange}
                onChange={(dateRange) => {
                  if (dateRange) {
                    setDateRange(dateRange);
                    resetTables();
                  }
                }}
              />
              <Filters
                pageObject="charges"
                onFilterUpdate={onFilterUpdate}
                instanceRef={filterInstanceRef}
                useStorage
              />
            </div>
            <BulkClaimsButton />
          </div>
        </div>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {viewType === 'Charges' && (
            <ChargesView
              dateRange={dateRange}
              searchQuery={searchQuery}
              filterQuery={filterQuery as ChargesFilter}
              tableInstanceRef={chargesTableInstanceRef as RefObject<VirtualTableInstance>}
            />
          ) }
          {viewType === 'Claims' && (
            <ClaimsView
              dateRange={dateRange}
              searchQuery={searchQuery}
              filterQuery={filterQuery as ChargesFilter}
              claimView={claimView}
              tableInstanceRef={claimsTableInstanceRef as RefObject<VirtualTableInstance>}
            />
          )}
        </div>
      </div>
      {!isNil(writeoffDialogParams) && (
        <WriteOffDialog
          {...writeoffDialogParams}
          onClose={() => { setWriteoffDialogParams(null); }}
        />
      )}
    </>

  );
}
